import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { COUNTRIES_DATA, COUNTRIES } from '../data/locations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTRY_CODES = {
  'Nigeria': '+234', 'Ghana': '+233', 'Kenya': '+254',
  'South Africa': '+27', 'Rwanda': '+250', 'Uganda': '+256', 'Tanzania': '+255'
};

const PROFESSIONAL_NEEDS = [
  'Architect', 'Civil Engineer', 'Structural Engineer', 'Mechanical Engineer',
  'Electrical Engineer', 'Quantity Surveyor', 'Builder', 'Project Manager',
  'Multiple Professionals', 'Other (specify)'
];

const PURPOSE_OPTIONS = [
  { value: 'project', label: 'Project-based (site, construction, inspection, etc.)' },
  { value: 'organization', label: 'Organization-based (internal role within company)' },
];

const PROJECT_TYPES = ['Residential', 'Commercial', 'Renovation', 'Site Inspection'];
const TIMELINES = ['Immediate', 'Within 1–2 weeks', 'Within 1 month'];

const RequestProfessional = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    full_name: '', phone: '', email: '', country: '', state: '',
    needs: [], other_profession: '', purpose: '', project_type: '', timeline: '', description: ''
  });

  const states = form.country ? COUNTRIES_DATA[form.country] || [] : [];
  const countryCode = COUNTRY_CODES[form.country] || '';

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'country') setForm(prev => ({ ...prev, country: value, state: '' }));
    else setForm(prev => ({ ...prev, [name]: value }));
  };

  const toggleNeed = (need) => {
    setForm(prev => ({
      ...prev,
      needs: prev.needs.includes(need) ? prev.needs.filter(n => n !== need) : [...prev.needs, need]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.country) { setError('Please select a country (required for WhatsApp dialing code)'); return; }
    if (!countryCode) { setError('Country dialing code could not be resolved. Please reselect the country.'); return; }
    if (form.needs.length === 0) { setError('Please select at least one professional need'); return; }
    if (form.needs.includes('Other (specify)') && !form.other_profession.trim()) {
      setError('Please specify the other profession(s) you need');
      return;
    }
    if (!form.purpose) { setError('Please select the purpose of your request'); return; }
    setLoading(true);
    try {
      const digits = form.phone.replace(/\D/g, '').replace(/^0+/, '');
      const fullPhone = countryCode + digits;
      const res = await axios.post(`${API_URL}/api/professional-requests`, { ...form, phone: fullPhone });
      setResult(res.data);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success && result) {
    return (
      <div className="min-h-screen bg-[#0B0F1A] text-slate-200 flex items-center justify-center px-6">
        <div className="max-w-lg w-full text-center space-y-6">
          <CheckCircle2 className="h-14 w-14 text-green-400 mx-auto" strokeWidth={1.5} />
          <h1 className="font-heading text-2xl tracking-tight text-slate-100">Request Received</h1>
          <p className="text-slate-400 text-sm">Your request has been received. Our team will contact you shortly.</p>
          <div className="bg-green-900/20 border-2 border-green-500/60 p-6 space-y-3 rounded">
            <div className="text-xs uppercase tracking-widest text-green-400 font-bold">YOUR REQUEST ID — SAVE THIS</div>
            <div className="text-2xl font-bold text-green-300 font-mono tracking-wider" data-testid="request-unique-id">{result.unique_id}</div>
            <div className="text-slate-300 font-semibold text-sm">Use this ID or your email to manage your request.</div>
            <div className="text-xs text-slate-400">Email: {result.email}</div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
            <button onClick={() => navigate('/retrieve')} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-xs uppercase tracking-wider font-semibold rounded transition-colors">Manage Profile & Requests</button>
            <button onClick={() => navigate('/')} className="border border-slate-700 text-slate-200 hover:border-slate-500 hover:bg-slate-800/40 px-6 py-3 text-xs uppercase tracking-wider font-semibold rounded transition-colors">Back to Home</button>
          </div>
        </div>
      </div>
    );
  }

  const inputClass = "w-full bg-slate-900/40 border border-slate-700 text-slate-100 h-11 px-4 text-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 rounded transition-colors";
  const labelClass = "text-xs uppercase tracking-widest text-slate-400 font-semibold mb-1.5 block";

  return (
    <div className="min-h-screen bg-[#0B0F1A] text-slate-200">
      <header className="sticky top-0 z-50 bg-[#0B0F1A]/90 backdrop-blur-xl border-b border-slate-800/60 h-16 flex items-center justify-center px-6">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
          <img src="https://customer-assets.emergentagent.com/job_submission-hub-21/artifacts/1pp9pqr0_1000260687.png" alt="BuildDirect Logo" className="h-10" />
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl tracking-tight mb-2 text-slate-100">Request a Building Professional</h1>
          <p className="text-slate-400 text-sm">For contractors, project owners, and organizations</p>
        </div>

        {error && (
          <div className="mb-6 bg-red-900/20 border border-red-500/40 p-3 flex items-start gap-2 rounded">
            <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Contact Info */}
          <div className="space-y-4">
            <h2 className="text-xs uppercase tracking-widest text-slate-400 font-semibold border-b border-slate-800 pb-2">Your Details</h2>
            <div>
              <label className={labelClass}>Full Name / Company Name *</label>
              <input type="text" name="full_name" required value={form.full_name} onChange={handleChange} data-testid="req-full-name" className={inputClass} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Country *</label>
                <select name="country" required value={form.country} onChange={handleChange} data-testid="req-country" className={inputClass}>
                  <option value="">Select Country</option>
                  {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>State / City *</label>
                <select name="state" required value={form.state} onChange={handleChange} data-testid="req-state" className={inputClass} disabled={!form.country}>
                  <option value="">{form.country ? 'Select' : 'Select country first'}</option>
                  {states.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Phone Number (WhatsApp) *</label>
                <div className="flex gap-2">
                  <div
                    data-testid="req-country-code"
                    className={`h-11 px-3 flex items-center rounded text-sm font-mono min-w-[72px] justify-center font-semibold ${
                      countryCode
                        ? 'bg-blue-900/30 border border-blue-500/40 text-blue-300'
                        : 'bg-amber-900/20 border border-amber-500/40 text-amber-300'
                    }`}
                    title={countryCode ? `Country dialing code: ${countryCode}` : 'Select country to auto-fill dialing code'}
                  >
                    {countryCode || 'Code *'}
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={form.phone}
                    onChange={handleChange}
                    placeholder={countryCode ? '8012345678' : 'Select country first'}
                    disabled={!countryCode}
                    data-testid="req-phone"
                    className={`${inputClass} ${!countryCode ? 'bg-slate-800/40 cursor-not-allowed' : ''}`}
                  />
                </div>
                <p className="text-[10px] text-slate-400 mt-1">
                  Dialing code is <span className="font-semibold text-slate-200">mandatory</span>. Enter number without leading zero — code is auto-added.
                </p>
              </div>
              <div>
                <label className={labelClass}>Email Address *</label>
                <input type="email" name="email" required value={form.email} onChange={handleChange} data-testid="req-email" className={inputClass} />
              </div>
            </div>
          </div>

          {/* What do you need */}
          <div className="space-y-4">
            <h2 className="text-xs uppercase tracking-widest text-slate-400 font-semibold border-b border-slate-800 pb-2">What do you need?</h2>
            <div className="flex flex-wrap gap-2">
              {PROFESSIONAL_NEEDS.map((need) => (
                <button type="button" key={need} onClick={() => toggleNeed(need)}
                  data-testid={`need-${need.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                  className={`px-3 py-2 text-xs font-semibold rounded transition-colors ${
                    form.needs.includes(need) ? 'bg-blue-600 text-white' : 'bg-slate-800/60 text-slate-300 hover:bg-slate-700/60 border border-slate-700'
                  }`}>
                  {need}
                </button>
              ))}
            </div>
            {form.needs.includes('Other (specify)') && (
              <div>
                <label className={labelClass}>Specify other profession(s) *</label>
                <input
                  type="text"
                  name="other_profession"
                  value={form.other_profession}
                  onChange={handleChange}
                  placeholder="e.g. Interior Designer, Solar Technician, Welder"
                  data-testid="req-other-profession"
                  className={inputClass}
                />
                <p className="text-[10px] text-slate-500 mt-1">Separate with commas if multiple.</p>
              </div>
            )}
          </div>

          {/* Purpose */}
          <div className="space-y-4">
            <h2 className="text-xs uppercase tracking-widest text-slate-400 font-semibold border-b border-slate-800 pb-2">Purpose of Request</h2>
            <div className="space-y-2">
              {PURPOSE_OPTIONS.map((opt) => (
                <label key={opt.value} className={`flex items-center gap-3 p-3 border rounded cursor-pointer transition-colors ${form.purpose === opt.value ? 'border-blue-500 bg-blue-900/20' : 'border-slate-700 hover:border-slate-500'}`}>
                  <input type="radio" name="purpose" value={opt.value} checked={form.purpose === opt.value} onChange={handleChange} className="text-blue-600 accent-blue-500" />
                  <span className="text-sm text-slate-300">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Project Details */}
          <div className="space-y-4">
            <h2 className="text-xs uppercase tracking-widest text-slate-400 font-semibold border-b border-slate-800 pb-2">Project / Service Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Project Type</label>
                <select name="project_type" value={form.project_type} onChange={handleChange} data-testid="req-project-type" className={inputClass}>
                  <option value="">Select</option>
                  {PROJECT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Timeline</label>
                <select name="timeline" value={form.timeline} onChange={handleChange} data-testid="req-timeline" className={inputClass}>
                  <option value="">Select</option>
                  {TIMELINES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={labelClass}>Briefly describe your need</label>
              <textarea name="description" value={form.description} onChange={handleChange} rows={3}
                placeholder="Tell us more about your project or requirement"
                data-testid="req-description"
                className="w-full bg-slate-900/40 border border-slate-700 text-slate-100 px-4 py-2.5 text-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 rounded transition-colors" />
            </div>
          </div>

          <button type="submit" disabled={loading} data-testid="submit-request-button"
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white py-3.5 text-xs uppercase tracking-wider font-semibold transition-colors rounded shadow-lg shadow-blue-600/20 hover:shadow-blue-600/40">
            {loading ? 'Submitting...' : 'Submit Request'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default RequestProfessional;
